import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SvgIcon, IconButton } from '@mui/material';
import { BiTrash } from 'react-icons/bi';
import cn from 'classnames';
import api from 'api';
import CheckboxWithLabel from 'components/common/CheckboxWithLabel/CheckboxWithLabel';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as ResendEmailIcon } from 'assets/icons/resendEmail.svg';
import AlertMessage, { AlertObj } from 'components/common/alert';
import styles from './UsersList.module.scss';
import { ImmigrationProfessional } from 'api';
import UsersListPopover from './UsersListPopover';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

type Props = {
    users: ImmigrationProfessional[];
    getSelectedItems: (selectedInvoices: ImmigrationProfessional[]) => void;
    removeItemClick: (id: number) => void;
};

const UsersList = ({ users, getSelectedItems, removeItemClick }: Props) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const [, setCurrentUser] = useState<ImmigrationProfessional>();
    const [checkedHeader, setCheckedHeader] = useState<boolean>(false);
    const [checkboxList, setCheckboxList] = useState<
        (ImmigrationProfessional & { checked: boolean })[]
    >([]);
    const [isShowingAlert, setShowingAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState<AlertObj>({ type: 'info', value: '' });

    const navigate = useNavigate();

    useEffect(() => {
        setCheckedHeader(false);

        const checkboxes = users.map((user) => {
            return {
                ...user,
                checked: false,
            };
        });
        setCheckboxList(checkboxes);
    }, [users]);

    useEffect(() => {
        const filteredList: (ImmigrationProfessional & { checked: boolean })[] =
            checkboxList.filter((item) => item.checked);
        const outputList: ImmigrationProfessional[] = filteredList.map((item) => ({
            ...item,
        }));

        getSelectedItems(outputList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkedHeader, checkboxList]);

    const handleChangeHeader = (checkedStatus: boolean) => {
        setCheckedHeader(checkedStatus);

        setCheckboxList((state) => {
            const modified = state.map((item) => {
                item.checked = checkedStatus;
                return item;
            });
            return [...modified];
        });
    };

    const handleChangeItem = (id: number, checkedStatus: boolean) => {
        const target = checkboxList.find((item) => item.id === id);

        if (target) {
            target.checked = checkedStatus;
            setCheckboxList((state) => [...state]);
        }

        const allListChecked: boolean = checkboxList.every((item) => item.checked);
        if (allListChecked) {
            setCheckedHeader(true);
        } else setCheckedHeader(false);
    };

    const handleEditClick = (id: number) => {
        navigate(`/account/permissions/edit-user/${id}`);
    };

    const handleResendClick = async (id: number) => {
        try {
            const request = await api.sendInvitation.sendInvitation(id);

            if (request.status === 200 || request.status === 201) {
                setAlertMessage({
                    type: 'success',
                    value: t('emailSuccessfullySent', { lng: language }),
                });
                setShowingAlert(true);
            }
        } catch {
            setAlertMessage({ type: 'error', value: t('somethingWentWrong', { lng: language }) });
            setShowingAlert(true);
        }
    };

    const Alert = (
        <AlertMessage
            type={alertMessage?.type}
            text={alertMessage?.value}
            open={isShowingAlert}
            onClose={() => setShowingAlert(false)}
        ></AlertMessage>
    );

    const formatRole = (role: string) => {
        const arrayRole = role.split('_');
        return arrayRole.join(' ').toLowerCase();
    };

    return (
        <div className={styles.list}>
            {Alert}
            <div className={cn(styles.row, styles.header)}>
                <div className={styles.wrapper}>
                    <div className={cn(styles.item, styles.name)}>
                        <CheckboxWithLabel
                            label={t('name', { lng: language })}
                            gap="16px"
                            bold
                            onChange={(ev) =>
                                handleChangeHeader((ev.target as HTMLInputElement).checked)
                            }
                            checked={checkedHeader}
                            disabled={checkboxList.length === 0}
                        />
                    </div>
                    <span className={cn(styles.item, styles.email)}>
                        {t('email', { lng: language })}
                    </span>
                    <span className={cn(styles.item, styles.status)}>
                        {t('status', { lng: language })}
                    </span>
                    <span className={cn(styles.item, styles.role)}>
                        {t('role', { lng: language })}
                    </span>
                    <span className={cn(styles.item, styles.action)}>
                        {t('actions', { lng: language })}
                    </span>
                </div>
            </div>
            {checkboxList.length === 0 && (
                <div className={styles.placeholder}>{t('theListIsEmpty', { lng: language })}</div>
            )}
            {checkboxList.map((user) => (
                <div key={user.id} className={styles.row}>
                    <div className={styles.wrapper}>
                        <div className={cn(styles.item, styles.name)}>
                            <CheckboxWithLabel
                                label={`${user.firstname} ${user.lastname}`}
                                gap="16px"
                                sx={{ width: '100%' }}
                                labelStyles={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    marginTop: '5px',
                                }}
                                checked={user.checked}
                                onChange={(__, checked) => handleChangeItem(user.id, checked)}
                            />
                        </div>
                        <Tooltip title={user.email} arrow>
                            <span className={cn(styles.item, styles.email)}>{user.email}</span>
                        </Tooltip>

                        <span className={cn(styles.item, styles.status)}>
                            {user.status === 'INVITED'
                                ? t('invited', { lng: language })
                                : t('confirmed', { lng: language })}
                        </span>

                        <Tooltip title={formatRole(user.role)} arrow>
                            <span className={cn(styles.item, styles.role)}>
                                {formatRole(user.role)}
                            </span>
                        </Tooltip>

                        <span className={cn(styles.item, styles.action)}>
                            <UsersListPopover
                                currentUser={user}
                                onUser={(value) => setCurrentUser(value)}
                                handleEditClick={handleEditClick}
                                removeItemClick={removeItemClick}
                                handleResendClick={handleResendClick}
                            />
                        </span>

                        {/* MOBILE VERSION */}
                        <div className={styles['mobile-version']}>
                            <div className={styles.item}>
                                <CheckboxWithLabel
                                    label={t('select', { lng: language })}
                                    gap="10px"
                                    labelStyles={{ color: '#686868' }}
                                    checked={user.checked}
                                    onChange={(__, checked) => handleChangeItem(user.id, checked)}
                                />
                            </div>
                            <section className={styles.item}>
                                <h5 style={{ minWidth: '80px' }} className={styles.title}>
                                    {t('name', { lng: language })}
                                </h5>
                                <span>{`${user.firstname} ${user.lastname}`}</span>
                            </section>
                            <section className={styles.item}>
                                <h5 style={{ minWidth: '80px' }} className={styles.title}>
                                    {t('email', { lng: language })}
                                </h5>
                                <span>{user.email}</span>
                            </section>
                            <section className={styles.item}>
                                <h5 style={{ minWidth: '80px' }} className={styles.title}>
                                    {t('status', { lng: language })}
                                </h5>
                                <span>
                                    {user.status === 'INVITED'
                                        ? t('invited', { lng: language })
                                        : t('confirmed', { lng: language })}
                                </span>
                            </section>
                            <section className={styles.item}>
                                <h5 style={{ minWidth: '80px' }} className={styles.title}>
                                    {t('role', { lng: language })}
                                </h5>
                                <span>{formatRole(user.role)}</span>
                            </section>
                            <section className={styles.item}>
                                <h5 style={{ minWidth: '80px' }} className={styles.title}>
                                    {t('actions', { lng: language })}
                                </h5>
                                <IconButton
                                    style={{
                                        fontFamily: 'Assistant, sans-serif',
                                        fontSize: '15px',
                                        color: '#282828',
                                        height: '36px',
                                        width: '36px',
                                        border: '2px solid rgb(231, 231, 231)',
                                        borderRadius: '4px',
                                        boxSizing: 'border-box',
                                        marginRight: '5px',
                                    }}
                                    onClick={() => handleEditClick(user.id)}
                                >
                                    <SvgIcon
                                        style={{ width: '12px', height: '12px' }}
                                        viewBox="7 4 8 12"
                                        component={EditIcon}
                                    />
                                </IconButton>

                                <IconButton
                                    style={{
                                        fontFamily: 'Assistant, sans-serif',
                                        fontSize: '15px',
                                        color: '#282828',
                                        height: '36px',
                                        width: '36px',
                                        border: '2px solid rgb(231, 231, 231)',
                                        borderRadius: '4px',
                                        boxSizing: 'border-box',
                                    }}
                                    onClick={() => removeItemClick(user.id)}
                                >
                                    <SvgIcon
                                        style={{ width: '15', height: '16.7px' }}
                                        viewBox="3 4 18 16.7"
                                        component={BiTrash}
                                    />
                                </IconButton>

                                {user.status === 'INVITED' && (
                                    <IconButton
                                        style={{
                                            fontFamily: 'Assistant, sans-serif',
                                            fontSize: '15px',
                                            color: '#282828',
                                            height: '36px',
                                            width: '36px',
                                            border: '2px solid rgb(231, 231, 231)',
                                            borderRadius: '4px',
                                            boxSizing: 'border-box',
                                            marginLeft: '5px',
                                        }}
                                        onClick={() => handleResendClick(user.id)}
                                    >
                                        <SvgIcon
                                            style={{ width: '23px', height: '23px' }}
                                            viewBox="0 -3 24 30"
                                            component={ResendEmailIcon}
                                        />
                                    </IconButton>
                                )}
                            </section>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default UsersList;
